import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { toast } from "sonner";
import { KeyRound, Save, Eraser, Activity } from "lucide-react";

/**
 * /admin/xgate — XGate runtime config back-office.
 *
 * Mirrors AdminEmail / AdminKycaid: masked secrets, per-field source label,
 * edit form, ping button. New role action `config.xgate` (admin + treasury).
 */
export default function AdminXgate() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({ api_base: "", api_token: "", webhook_secret: "", checkout_base: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);

  const loadCfg = async () => {
    try { const r = await api.get("/admin/xgate/config"); setCfg(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load XGate config"); }
  };
  useEffect(() => { loadCfg(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      Object.entries(draft).forEach(([k, v]) => { if (v !== "") patch[k] = v; });
      const r = await api.patch("/admin/xgate/config", patch);
      setCfg(r.data);
      setEditOpen(false);
      setDraft({ api_base: "", api_token: "", webhook_secret: "", checkout_base: "" });
      toast.success("XGate config updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const clearOverrides = async () => {
    if (!window.confirm("Clear all XGate overrides? Falls back to env vars.")) return;
    try {
      const r = await api.patch("/admin/xgate/config", {
        api_base: "", api_token: "", webhook_secret: "", checkout_base: "",
      });
      setCfg(r.data);
      toast.success("Overrides cleared.");
    } catch (e) { toast.error("Failed to clear"); }
  };

  const ping = async () => {
    setPinging(true);
    setPingResult(null);
    try {
      const r = await api.post("/admin/xgate/ping");
      setPingResult(r.data);
    } catch (e) {
      setPingResult({ ok: false, error: e?.response?.data?.detail || String(e) });
    } finally { setPinging(false); }
  };

  if (!cfg) return <AdminLayout title="XGate"><div className="text-sm text-muted-foreground">Loading…</div></AdminLayout>;

  const anySettingsSource = Object.values(cfg.source || {}).includes("settings");

  return (
    <AdminLayout
      title="XGate API connection"
      subtitle="Rotate the XGate API token / webhook secret / checkout base without a restart. Required for live refund dispatch and balance-API treasury checks."
    >
      <div className="xch-card p-5 mb-5" data-testid="xgate-config-card">
        <div className="flex items-start gap-3 flex-wrap">
          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 border ${cfg.wired_for_api ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            <KeyRound className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-bold text-foreground">Runtime config</div>
              <Badge variant="outline"
                     className={`text-[10px] uppercase tracking-wider font-bold ${cfg.wired_for_api ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
                     data-testid="xgate-config-wired-badge">
                {cfg.wired_for_api ? "Wired for API" : "API not wired"}
              </Badge>
              {cfg.webhook_secret_set && (
                <Badge variant="outline" className="text-[10px] uppercase font-bold bg-sky-50 text-sky-700 border-sky-200"
                       data-testid="xgate-config-webhook-secret-badge">
                  Webhook secret set
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1 mt-2 text-xs">
              <div className="text-muted-foreground">API base</div>
              <div className="font-mono text-[11px] text-foreground md:col-span-2" data-testid="xgate-config-api-base">
                {cfg.api_base || <em className="text-muted-foreground">not set</em>}
                <span className="ml-2 text-[10px] text-muted-foreground">(source: {cfg.source.api_base})</span>
              </div>
              <div className="text-muted-foreground">API token</div>
              <div className="font-mono text-[11px] text-foreground md:col-span-2" data-testid="xgate-config-api-token-mask">
                {cfg.api_token_set ? cfg.api_token_mask : <em className="text-muted-foreground">not set</em>}
                <span className="ml-2 text-[10px] text-muted-foreground">(source: {cfg.source.api_token})</span>
              </div>
              <div className="text-muted-foreground">Webhook secret</div>
              <div className="font-mono text-[11px] text-foreground md:col-span-2">
                {cfg.webhook_secret_set ? cfg.webhook_secret_mask : <em className="text-muted-foreground">not set</em>}
                <span className="ml-2 text-[10px] text-muted-foreground">(source: {cfg.source.webhook_secret})</span>
              </div>
              <div className="text-muted-foreground">Checkout base</div>
              <div className="font-mono text-[11px] text-foreground md:col-span-2">
                {cfg.checkout_base}
                <span className="ml-2 text-[10px] text-muted-foreground">(source: {cfg.source.checkout_base})</span>
              </div>
            </div>
            {!editOpen && (
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button onClick={() => setEditOpen(true)} variant="outline" size="sm"
                        className="rounded-xl gap-1.5" data-testid="xgate-config-edit-btn">
                  <KeyRound className="w-3.5 h-3.5" /> Rotate / set creds
                </Button>
                {anySettingsSource && (
                  <Button onClick={clearOverrides} variant="outline" size="sm"
                          className="rounded-xl gap-1.5" data-testid="xgate-config-clear-btn">
                    <Eraser className="w-3.5 h-3.5" /> Clear overrides
                  </Button>
                )}
                <Button onClick={ping} variant="outline" size="sm"
                        disabled={pinging || !cfg.wired_for_api}
                        className="rounded-xl gap-1.5" data-testid="xgate-config-ping-btn">
                  <Activity className="w-3.5 h-3.5" /> {pinging ? "Pinging…" : "Ping sandbox"}
                </Button>
              </div>
            )}
            {editOpen && (
              <div className="mt-3 space-y-2.5 rounded-xl border border-border bg-muted/30 p-3" data-testid="xgate-config-edit-form">
                <FormField label="API base" placeholder="https://sandbox.xgateway.app/api"
                            value={draft.api_base}
                            onChange={(v) => setDraft({ ...draft, api_base: v })}
                            testid="xgate-config-api-base-input" />
                <FormField label="API token" placeholder="paste new bearer token"
                            type="password" value={draft.api_token}
                            onChange={(v) => setDraft({ ...draft, api_token: v })}
                            testid="xgate-config-api-token-input" />
                <FormField label="Webhook secret" placeholder="callback signing secret"
                            type="password" value={draft.webhook_secret}
                            onChange={(v) => setDraft({ ...draft, webhook_secret: v })}
                            testid="xgate-config-webhook-input" />
                <FormField label="Checkout base (optional)" placeholder="https://checkout.xgateway.app/sepa"
                            value={draft.checkout_base}
                            onChange={(v) => setDraft({ ...draft, checkout_base: v })}
                            testid="xgate-config-checkout-input" />
                <div className="flex gap-2 justify-end">
                  <Button onClick={() => { setEditOpen(false); setDraft({ api_base: "", api_token: "", webhook_secret: "", checkout_base: "" }); }}
                          variant="ghost" size="sm" className="rounded-xl">Cancel</Button>
                  <Button onClick={save}
                          disabled={saving || Object.values(draft).every((v) => v === "")}
                          className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                          data-testid="xgate-config-save-btn">
                    <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  Leave blank to keep current value. Changes apply immediately to refund dispatch + balance API calls.
                </div>
              </div>
            )}
            {pingResult && (
              <div className={`mt-3 rounded-xl border p-3 text-xs ${pingResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}
                   data-testid="xgate-ping-result">
                <div className="font-display font-bold">Ping result</div>
                <div className="font-mono text-[11px] mt-1">
                  {pingResult.status_code ? `HTTP ${pingResult.status_code}` : pingResult.status || "—"}
                  {pingResult.reason ? ` — ${pingResult.reason}` : ""}
                  {pingResult.error ? ` — ${pingResult.error}` : ""}
                </div>
                {pingResult.snippet && <div className="font-mono text-[10px] mt-1 break-all opacity-70">{pingResult.snippet}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="text-[11px] text-muted-foreground" data-testid="xgate-help">
        Until <strong>API base</strong> + <strong>API token</strong> are set, refund dispatch falls back to a manual stub ticket (<code>xg_refund_stub_…</code>).
        Once wired, dispatch calls XGate's <code>/withdrawals</code> directly and a withdrawal-completed callback closes the refund automatically.
      </div>
    </AdminLayout>
  );
}

function FormField({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)}
             placeholder={placeholder} className="rounded-xl mt-1" data-testid={testid} />
    </div>
  );
}
